// Registry of sales data sources shown on the Integrations screen.
//
// Adding a source is meant to require only an entry here plus its OAuth flow
// (DEV-2211 for Shopify, DEV-2216 for Amazon): `startConnect` starts undefined
// and each ticket fills it in with a function that kicks off that source's
// authorization. Until then ConnectCard renders the source disabled with
// "Available soon" so the screen is correct before either flow lands.
//
// `startConnect(advertiserId, authToken)` may return a Promise or nothing. A
// rejected Promise is caught by SalesIntegrationsSection and shown as a
// connect failure on that source's card.
//
// `startConnect`, `authorizeConnect` and `confirmConnect` may be getters that return undefined
// while a source's flow is switched off (Amazon, REACT_APP_AMAZON_CONNECT_ENABLED),
// so read them from the entry at the point of use; never cache them.
//
// `id` must match the `source` column in `sales_integrations.integration_connections`
// (migration 079: 'shopify' | 'amazon').
//
// `iconPaths` are stroke paths on a 24x24 grid, drawn in `color.text`. They are
// generic commerce glyphs (a shopping bag, a parcel), not the vendors' logos.
//
// `appStore`, when present, replaces the direct Connect button for a source
// whose first-time connect must start on the vendor's own surface (Shopify App
// Store requirement 2.3.1: never ask a merchant to type a shop domain).
// `getListingUrl()` returns the listing URL (read lazily from the environment;
// empty/undefined until the listing is approved), `actionLabel` is the short
// visible link text (it must fit the card's action column in the narrow
// Integrations panel), `actionAriaLabel` its full accessible name, and
// `guidance` is the plain-text fallback shown when there is no URL.
// ConnectCard shows this action whenever the source has no usable connection.
//
// `reconnectValue(connection)`, when present, returns the value to hand to
// `startConnect(advertiserId, authToken, value)` when reconnecting an existing
// connection that needs attention, taken from the connection itself so the
// merchant never types it. When it returns nothing, ConnectCard falls back to
// the `appStore` action.
//
// `postConnectStep`, when present, adds a quiet "one more step" block to a
// healthy connected card (Shopify uses it for the theme app embed: App Store
// rule 5.1.1 forbids editing the merchant's theme, 5.1.3 wants clear onboarding
// instructions). `getUrl(connection)` returns the link, or nothing when it
// cannot be built; `linkLabel` is the short visible text and `linkAriaLabel`
// its full accessible name. With no URL the card shows `fallbackBody` as plain
// text instead of `body` and the link.
//
// Optional `getState(connection)` (DEV-2400) returns "done" | "pending" |
// "todo" (also the result when absent). "done": the card shows `doneLabel`
// as one quiet line instead of the step. "pending": checked and not done yet,
// so the step shows with `delayNote` under it. "todo": the step, no note.
// Optional `delayNoteApplies(connection, nowMs)` (DEV-2405) can suppress the note
// for a pending step: Shopify uses it to drop the "it can take a while" sentence
// when the "off" was observed within the last 15 seconds. A pure function of the
// stored columns. Absent means always show it.
//
// `confirmConnect(advertiserId, confirmToken, authToken)`, when present, is
// called once by SalesIntegrationsSection after a `?status=pending&confirm=`
// redirect for this source (code-review MEDIUM #2's confirm-in-app step).
// Optional per source: only an OAuth flow that stages a pending connection
// needs it.
//
// `authorizeConnect(advertiserId, authToken, login)`, when present, is the
// second step of a flow whose vendor sends the user back to our Login URI
// before granting access (Amazon, DEV-2216). AuthGate captures and persists
// the vendor's params, and SalesIntegrationsSection calls this only from an
// explicit "Connect <name> <accountNoun> to <advertiser>?" prompt.
//
// `clearPendingAuthorize()`, present with `authorizeConnect`, drops the
// persisted Login URI request once the prompt is acted on.
//
// `connectRegions`, when present, is a list of { value, label } that
// ConnectCard renders as a small labeled select next to Connect; the chosen
// value is passed to `startConnect(advertiserId, authToken, value)`. Amazon
// uses it so a European or Far East seller lands on their own Seller Central.
//
// `memberOnlyConnect`, when true, disables Connect for a support user (support
// masquerade is read-only) with a short note, because the source's OAuth
// function rejects support callers. Shopify does not set it.

import { confirmShopifyConnect, startShopifyConnect } from "../../services/shopifyService";
import { authorizeAmazonConnect, confirmAmazonConnect, startAmazonConnect } from "../../services/amazonService";
import { amazonConnectEnabled, clearAmazonPendingAuthorize } from "../../lib/amazonPendingAuthorize";
import { buildThemeEmbedUrl, isCheckedWithin, THEME_EMBED_NOTE_QUIET_MS } from "./shopifyThemeEmbed";

export const SALES_SOURCES = [
  {
    id: "shopify",
    name: "Shopify",
    description: "Sync your Shopify orders to measure the sales your audio ads drive.",
    // A generic storefront in neutral grey, deliberately not a green shopping
    // bag: Shopify's App Store listing check reads that as the Shopify S-bag
    // mark (listing guidelines forbid it outside a clear integration label).
    iconPaths: ["M3 9l1.5-5h15L21 9", "M3 9h18v2a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0z", "M5 13v7h14v-7", "M10 20v-4h4v4"],
    color: { bg: "#F5F5F5", border: "#E2E2E2", text: "#3E3E3E" },
    appStore: {
      getListingUrl: () => process.env.REACT_APP_SHOPIFY_APP_LISTING_URL,
      actionLabel: "Install on Shopify",
      actionAriaLabel: "Install Audiohook Sales Sync from the Shopify App Store",
      guidance: "Install Audiohook Sales Sync from the Shopify App Store to connect your store.",
    },
    postConnectStep: {
      getUrl: (connection) =>
        buildThemeEmbedUrl({
          shop: connection?.external_account_id,
          // Read lazily so tests can change it per call.
          clientId: process.env.REACT_APP_SHOPIFY_APP_CLIENT_ID,
        }),
      heading: "Turn on visitor tracking in your theme",
      body: "This lets Audiohook match orders to the ads that drove them. Open your theme editor, check that Audiohook Cart Sync is on under App embeds, then click Save.",
      linkLabel: "Open theme editor",
      linkAriaLabel: "Open theme editor to turn on Audiohook Cart Sync",
      fallbackBody: "In your Shopify admin, go to Online Store, Themes, Customize, then App embeds, and turn on Audiohook Cart Sync.",
      // theme_embed_enabled is written by the cycle-scheduled-tasks worker
      // (migration 091) and, when the panel opens, by the integrations function's
      // theme-embed-status action (DEV-2405): true = on, false = checked and off
      // (a to-do, not a failure), null/undefined = not checked yet or unknown.
      getState: (connection) => {
        // An observation made before the store was last connected says
        // nothing about the theme now (an uninstall and reinstall can turn the
        // embed off), so it is not trusted until the worker checks again.
        const checkedAt = Date.parse(connection?.theme_embed_checked_at);
        const connectedAt = Date.parse(connection?.connected_at);
        if (checkedAt < connectedAt) return "todo";
        if (connection?.theme_embed_enabled === true) return "done";
        if (connection?.theme_embed_enabled === false) return "pending";
        return "todo";
      },
      doneLabel: "Visitor tracking is on",
      // The worker's check is periodic, so after the merchant saves the embed the
      // step can stay until the next pass.
      delayNote: "It can take a little while to show here after you save.",
      // A just-observed "off" is current, so the sentence above only applies to an
      // answer that is stale or was never observed (the worker's check is periodic).
      delayNoteApplies: (connection, nowMs) => !isCheckedWithin(connection?.theme_embed_checked_at, nowMs, THEME_EMBED_NOTE_QUIET_MS),
    },
    // A Shopify connection's external_account_id is the store's myshopify.com domain.
    reconnectValue: (connection) => connection?.external_account_id || undefined,
    startConnect: (advertiserId, authToken, shop) => startShopifyConnect(advertiserId, authToken, shop),
    // installNonce (DEV-2332 code-review round) is optional -- present only
    // for an install-origin confirm; the ordinary bound (in-app) flow calls
    // this with no fourth argument.
    confirmConnect: (advertiserId, confirmToken, authToken, installNonce) =>
      confirmShopifyConnect(advertiserId, confirmToken, authToken, installNonce),
  },
  {
    id: "amazon",
    name: "Amazon",
    description: "Sync your Amazon orders to measure the sales your audio ads drive.",
    iconPaths: ["M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9z", "M3 7.5 12 12l9-4.5", "M12 12v9"],
    color: { bg: "#FFF7ED", border: "#FED7AA", text: "#EA580C" },
    // DEV-2216: Seller Central consent, then our Login URI (captured by
    // AuthGate, confirmed in the Integrations panel), then the callback and
    // the usual confirm step. Off unless REACT_APP_AMAZON_CONNECT_ENABLED is
    // "true" (read lazily, like getListingUrl): amazon-oauth is not deployed
    // until Amazon approves the developer registration (DEV-2207), and with
    // no startConnect the card stays "Available soon".
    get startConnect() {
      return amazonConnectEnabled() ? startAmazonConnect : undefined;
    },
    // SP-API regions; amazon-oauth's `start` builds the consent URL on the
    // region's main Seller Central host. North America first (the default).
    connectRegions: [
      { value: "na", label: "North America" },
      { value: "eu", label: "Europe" },
      { value: "fe", label: "Far East (Japan, Australia, Singapore)" },
    ],
    // amazon-oauth rejects support callers on start and authorize.
    memberOnlyConnect: true,
    get authorizeConnect() {
      return amazonConnectEnabled() ? authorizeAmazonConnect : undefined;
    },
    clearPendingAuthorize: clearAmazonPendingAuthorize,
    // What one connected account is called in copy (default "store"); the
    // authorize prompt reads "<name> <accountNoun>".
    accountNoun: "seller account",
    get confirmConnect() {
      return amazonConnectEnabled() ? confirmAmazonConnect : undefined;
    },
  },
];
